<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'
import type { PropType, ComputedRef } from 'vue'
import { useStore } from '@nanostores/vue'
import { createReusableTemplate } from '@vueuse/core'
import { vTooltip } from 'floating-vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import useProductSharing from '@/legacy/ts/useProductSharing'
import { useBreakpoints, getVueAssetImagePath } from '@/lib/utils'
import {
	kFormatter,
	MemberTypes,
	productSharingData,
	ProductSharingImageSize,
	getProductSharingUrl,
	getTimeAgoValue,
	checkExpressCheckoutEnable,
	productSharingPostPerPage,
} from '@/legacy/ts/productSharing'
import transformTextWithHashtag from '@/legacy/ts/checkHashtags'
import BsLoader from '@/legacy/components/Loader.vue'
import BsButtonNormal from '@/legacy/components/ButtonNormal.vue'
import BsButtonOutline from '@/legacy/components/ButtonOutline.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseTag from '@/components/ui/tag/BaseTag.vue'
import BsInfoSection from '@/legacy/components/InfoSection.vue'
import BsSideDrawer from '@/legacy/components/SideDrawer.vue'
import BsHowItWorksSkeleton from '@/legacy/components/HowItWorksSkeleton.vue'
import BsGallerySlideshow from '@/legacy/components/GallerySlideshow.vue'
import BsStickyBottomContainer from '@/legacy/components/StickyBottomContainer.vue'
import { InfoSectionTheme } from '@/types/InfoSectionLegacy'
import {
	Name as WebComponentNameList,
	createPostReminderHowItWorksView,
	createCheckoutShippingHowItWorksView,
} from '@/legacy/ts/webComponents.js'
import { ButtonNormalTheme } from '@/types/ButtonLegacy'
import { getformatFloatPrice } from '@/assets/ts/currency'
import ProductSharingEmptyContent from '@/legacy/components/ProductSharingEmptyContent.vue'
import { getInfoBoxContent } from '@/legacy/ts/getMonth'
import BsNoticeBlock from '@/legacy/components/NoticeBlock.vue'
import { NoticeBlockIconType } from '@/types/NoticeBlockLegacy'
import injectStrict from '@/lib/inject'
import BsIconButton from '@/legacy/components/IconButton.vue'
import ProductSharingPostList from '@/legacy/components/ProductSharingPostList.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import BaseAttentionBox from '@/components/ui/attentionbox/BaseAttentionBox.vue'
import DialogFull from '@/legacy/components/DialogFull.vue'
import { fromPostId } from '@/store/productSharingStore'
import {
	instanceProductSharing,
	fetchProductSharingSettings as fetchProductSharingSettingsApi,
} from '@/assets/ts/apiManagement'
import { getSiteBaseUrl, getMcrRootUrl } from '@/lib/siteBaseUrl'
import {
	isRegionSupportBuyforyou,
	checkIsNewCommunityPost,
} from '@/lib/checkService'

// in-app-solution
import { useAppOpener } from '@/lib/useAppOpener'

const eventInfo = {
	discover: {
		author: 'c_post_author_f_ps_discover_post',
		like: 'c_like_f_ps_discover_post',
		bookmark: 'c_bookmark_f_ps_discover_post',
		hashtag: 'c_hashtag_f_ps_discover_post',
		seeMore: 'c_see_more_f_ps_discover_post',
	},
	postDetail: {
		author: 'c_post_author_f_ps_post_detail',
		like: 'c_like_f_ps_post_detail',
		bookmark: 'c_bookmark_f_ps_post_detail',
		share: 'c_share_post_f_ps_post_detail',
		report: 'c_report_post_f_ps_post_detail',
		hashtag: 'c_hashtag_f_ps_post_detail',
		productCard: 'c_product_card_buy_f_ps_post_detail',
		openProductUrl: 'c_open_product_url_f_ps_post_detail',
		openBuyforyou: 'c_open_b4u_f_ps_post_detail',
		options: 'c_post_options_f_ps_post_detail',
	},
}

const props = defineProps({
	text: {
		type: Object,
	},
	detail: {
		type: null as unknown as PropType<any>,
		required: true,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	isTypeModal: {
		type: Boolean,
		default: false,
	},
	categories: {
		type: Array,
	},
	warehouses: {
		type: Object as PropType<any>,
	},
	supportWarehouses: {
		type: Array as PropType<any>,
	},
	interest: {
		type: Object as PropType<any>,
	},
	currencies: {
		type: Object as PropType<any>,
	},
	priceLocal: {
		type: String,
	},
	priceLocalPost: {
		type: String,
	},
	isShowContentBlock: {
		type: Boolean,
		default: false,
	},
	localCurrencyInfo: {
		type: Array as PropType<any>,
	},
	postListTextInfo: {
		type: Object,
	},
})

// in-app-solution
const { isInAppBrowser, openApp, openAppWithCheckout } = useAppOpener()

const [DefinePostAuthorContent, ReusePostAuthorContent] =
	createReusableTemplate()

const [DefinePostDetailContent, ReusePostDetailContent] =
	createReusableTemplate()

const [DefinePostBottomContent, ReusePostBottomContent] =
	createReusableTemplate()

const [DefineAboutShoppingContent, ReuseAboutShoppingContent] =
	createReusableTemplate<{
		shoppingItems: {
			id: string
			title: string
			icon: string
			action: () => void
		}[]
	}>()

const [DefineInfoItem, ReuseInfoItem] = createReusableTemplate<{
	iconName: string
	title: string
	value: string
}>()

const fromPostIdGlobal = useStore(fromPostId)
const { isTabletPortrait: isShowDesktopLayout } = useBreakpoints()
const { isHasPSProfile, PSUsername } = useProductSharing()
const addMessage: (message: string) => void = injectStrict('addMessage')
const setPostListInfoProvide: ((payload: any) => void) | null = injectStrict(
	'setPostListInfoProvide',
	null,
)
const emit = defineEmits(['update-post', 'update-list'])

const shareCopyRef = ref<HTMLElement | null>(null)
const isShowDrawerRestrictions = ref(false)
const isWebComponentLoading = ref(false)
const isShippingWebComponentLoading = ref(false)
const infoSectionData = {
	theme: InfoSectionTheme.Info,
	text: props.text?.infoSection.text,
	arrowText: props.text?.infoSection.arrowText,
}
const sideDrawerTitleRestrictions = ref('')
const isExpressCheckoutEnable = checkExpressCheckoutEnable()
const currentInfoBoxContent = ref<{
	title: string
	description: string
} | null>(null)
const isRecommendShow = ref(true)
const isAcceptReturnRefund = ref(false)
const isShowShippingHowItWork = ref(false)
const shippingHowItWorkTitle = ref('')

const postImageList = computed(() => {
	if (props.isLoading || !props.detail || !Object.keys(props.detail).length)
		return []
	return props.detail.media.map((item: any) => ({
		id: item.id,
		type: item.type.replace('ps_', ''),
		content: getMedia(item),
		preview: item.type.includes('video') ? item.preview : null,
	}))
})

const posterAvatar = computed(() => {
	return props.detail && props.detail.userInfo && props.detail.userInfo.image
		? getPosterImage(props.detail.userInfo.image)
		: getVueAssetImagePath('avatar_2_48px@2x.png')
})

const isSupportExpressCheckout = computed(() => {
	return (props.detail.isExpressCheckout && isExpressCheckoutEnable) || false
})

const productInfo = computed(() => {
	if (!props.text || !props.detail || !props.detail.itemInfo) return []
	const productInfo = props.detail.itemInfo
	const hideConvertedPriceForExpressCheckout = isSupportExpressCheckout.value
		? `${getPricePost(true)}`
		: `${getPricePost(true)} (${props.priceLocalPost})`

	return [
		{
			title: props.text.title.itemName,
			value:
				isSupportExpressCheckout.value && productInfo.ecName
					? productInfo.ecName
					: productInfo.name,
			icon: 'product',
		},
		{
			title: isSupportExpressCheckout.value
				? props.text.title.unitPriceEc
				: props.text.title.unitPrice,
			value: hideConvertedPriceForExpressCheckout,
			icon: 'price-tag',
		},
		{
			title: props.text.title.discounted,
			value: productInfo.discounted
				? props.text.discounted[productInfo.discounted]
				: '',
			icon: 'saved-money',
		},
		{
			title: props.text.title.paymentMethod,
			value: productInfo.paymentMethod
				? props.text.paymentMethod[productInfo.paymentMethod]
				: '',
			icon: 'credit-card',
		},
	].filter(item => item.value !== '')
})

const shippingInfo = computed(() => {
	if (!props.text || !props.detail || !props.detail.itemInfo) return []
	const productInfo = props.detail.itemInfo
	return [
		{
			title: props.text.title.warehouse,
			value: warehouseName.value,
			icon: 'warehouse',
		},
		{
			title: props.text.title.location,
			value:
				productInfo.location &&
				window.BUYANDSHIP.settings.all_countries &&
				window.BUYANDSHIP.settings.all_countries[productInfo.location]
					? window.BUYANDSHIP.settings.all_countries[productInfo.location].name
					: '',
			icon: 'delivery',
		},
		{
			title: props.text.title.weight,
			value: `${productInfo.weight} ${props.text.unitPound}`,
			icon: 'weight',
		},
	].filter(item => item.value !== '')
})

const shoppingItems = computed(() => {
	return [
		{
			id: 'shipping-process',
			title: props.text?.newUI.shoppingItems.shippingProcess,
			icon: 'community_post_plane',
			action: () => {
				createGAEvent(GaGeneralEvent.ButtonClick, {
					page_name: GaGeneralPageName.CommunityPostDetailPage,
					button_name: 'Tutorial Shipping Process',
					page_object_id: `post_${props.detail.id}`,
					post_id: props.detail.id,
					product_id: props.detail.productSlug,
					is_express_checkout: isSupportExpressCheckout.value,
				})

				showShippingHowItWork()
			},
		},
		{
			id: 'shopping-protection',
			title: props.text?.newUI.shoppingItems.shoppingProtection,
			icon: 'shopping_protection',
			action: () => {
				createGAEvent(GaGeneralEvent.ButtonClick, {
					page_name: GaGeneralPageName.CommunityPostDetailPage,
					button_name: 'Tutorial Shipping Protection',
					page_object_id: `post_${props.detail.id}`,
					post_id: props.detail.id,
					product_id: props.detail.productSlug,
					is_express_checkout: isSupportExpressCheckout.value,
				})

				window.location.href = `${getSiteBaseUrl()}/buyforyou/goods-protection/#basic`
			},
		},
		{
			id: 'item-restrictions',
			title: props.text?.newUI.shoppingItems.itemRestrictions,
			icon: 'community_post_warning',
			action: () => {
				createGAEvent(GaGeneralEvent.ButtonClick, {
					page_name: GaGeneralPageName.CommunityPostDetailPage,
					button_name: 'Prohibited Items',
					page_object_id: `post_${props.detail.id}`,
					post_id: props.detail.id,
					product_id: props.detail.productSlug,
					is_express_checkout: isSupportExpressCheckout.value,
				})

				window.location.href = `${getSiteBaseUrl()}/restrictions/`
			},
		},
	].filter(item => {
		const isItemRestrictionsHidden =
			item.id === 'item-restrictions' && !isDifferentCountry.value
		const isShoppingProtectionHidden =
			item.id === 'shopping-protection' &&
			window.BUYANDSHIP.settings.current_country !== 'hk'
		return (
			item.title && !isItemRestrictionsHidden && !isShoppingProtectionHidden
		)
	})
})

const categoryName = computed(() => {
	const match = props.categories?.filter((item: any) =>
		item.includes(props.detail.itemInfo.category),
	)[0] as any
	return match && match[1]
})

const interestName = computed(() => {
	return props.interest && props.interest[props.detail.interest].name
})

const warehouseName = computed(() => {
	if (!props.warehouses || !props.warehouses[props.detail.itemInfo.warehouse])
		return null
	return props.warehouses[props.detail.itemInfo.warehouse].name
})

const postInfo = computed(() => {
	if (!props.detail || !props.detail.itemInfo) return []
	return [
		{
			title: props.text?.title.category,
			value: categoryName.value,
			icon: 'product-category',
		},
		{
			title: props.text?.title.interest,
			value: interestName.value,
			icon: 'post-category',
		},
	].filter(item => item.value !== '')
})

const currencySymbol = computed(() => {
	if (!props.currencies || !props.detail.itemInfo.warehouse) return null
	if (!props.warehouses?.[props.detail.itemInfo.warehouse]) return null
	if (
		props.warehouses?.[props.detail.itemInfo.warehouse].currency === undefined
	)
		return null
	const { currency } = props.warehouses[props.detail.itemInfo.warehouse]
	return props.currencies[currency.toLowerCase()].symbol
})

const postContent = computed(() => {
	if (!props.detail.content) return ''
	return transformTextWithHashtag(props.detail.content)
})

const isBuyforyouEnable = computed(() => {
	if (!props.supportWarehouses || !Object.keys(props.detail).length)
		return false
	const warehouseMatchIndex = props.supportWarehouses.findIndex(
		(v: any) => v === props.detail.itemInfo.warehouse,
	)
	return isRegionSupportBuyforyou.value && warehouseMatchIndex !== -1
})

const isDifferentCountry = computed(() => {
	if (props.detail.itemInfo) {
		return (
			window.BUYANDSHIP.settings.current_country !==
			props.detail.itemInfo.location
		)
	}
	return false
})

const eventParams = computed(() => {
	return {
		category: props.detail.productSlug || 'none',
		label: props.detail.id,
		value: 'public_detail',
	}
})

const eventParamsProductSlug = computed(() => {
	return {
		category: props.detail.productSlug || 'none',
		product_slug: props.detail.productSlug || 'none',
		label: props.detail.id,
		post_id: props.detail.id,
		value: 'public_detail',
		from: 'public_detail',
		from_post_id: currentFromPostId.value,
	}
})

const infoDisplay: ComputedRef<any> = computed(() => {
	return {
		product: productInfo,
		shipping: shippingInfo,
		post: postInfo,
	}
})

const likeCountDisplay = computed(() => {
	if (props.detail.likeCount) {
		return kFormatter(props.detail.likeCount, props.text).toString()
	}
	return '0'
})

const postService = computed(() => {
	if (isSupportExpressCheckout.value) return 'easybuy'
	if (isBuyforyouEnable.value) return 'b4u'
	return 'bns'
})

const isLocalCurrency = computed(() => {
	if (
		props.localCurrencyInfo &&
		props.localCurrencyInfo.length &&
		isSupportExpressCheckout.value
	) {
		const temp: any = props.localCurrencyInfo.filter(
			(item: any) => item.key === props.detail.warehouse,
		)
		if (temp.length) {
			return temp[0].isLocal
		}
		return false
	}
	return false
})

const localPricing = computed(() => {
	if (isLocalCurrency.value) {
		return getformatFloatPrice({
			price: props.detail.itemInfo.ecLocalPrice,
		})
	}
	return ''
})

// Helper function to calculate discounted price while preserving original decimal formatting
function calculateDiscountedPriceWithDecimalPreservation(
	originalPriceString: string,
	discountPercentage: number,
	currencySymbol: string,
) {
	const purePrice = Number(originalPriceString)
	const calculatedPrice = purePrice / ((100 - discountPercentage) / 100)

	// Determine decimal places from original price string
	const decimalIndex = originalPriceString.indexOf('.')
	const decimalPlaces =
		decimalIndex === -1 ? 0 : originalPriceString.length - decimalIndex - 1

	// Format the calculated price with the same decimal places as original
	const formattedPrice = calculatedPrice.toLocaleString('en', {
		minimumFractionDigits: decimalPlaces,
		maximumFractionDigits: decimalPlaces,
	})

	return `${currencySymbol} ${formattedPrice}`
}

const reportedLocalPrice = computed(() => {
	const originalPriceString = props.priceLocalPost?.replace(/[^\d.]/g, '') || ''
	return calculateDiscountedPriceWithDecimalPreservation(
		originalPriceString,
		Number(props.detail.itemInfo.discounted),
		window.BUYANDSHIP.settings.current_country2.currency_symbol || '',
	)
})

const reportedLocalPriceInOriginalCurrency = computed(() => {
	const originalPriceString = getPricePost(true).replace(/[^\d.]/g, '')
	return calculateDiscountedPriceWithDecimalPreservation(
		originalPriceString,
		Number(props.detail.itemInfo.discounted),
		currencySymbol.value,
	)
})

const displayedDiscountFromReportedPrice = computed(() => {
	return props.text?.newUI.discountFromReportedPrice.replace(
		'{PERCENTAGE}',
		`${props.detail.itemInfo.discounted}%`,
	)
})

const displayedDiscountFromReportedPriceDesktop = computed(() => {
	return props.text?.newUI.discountFromReportedPriceDesktop.replace(
		'{PERCENTAGE}',
		`${props.detail.itemInfo.discounted}%`,
	)
})

const isMemberOfficial1 = computed(() => {
	if (props.detail?.userInfo?.memberTypes?.length) {
		return props.detail.userInfo.memberTypes.includes(MemberTypes.Official1)
	}
	return false
})

const isMemberAssigned1 = computed(() => {
	if (props.detail?.userInfo?.memberTypes?.length) {
		return props.detail.userInfo.memberTypes.includes(MemberTypes.Assigned1)
	}
	return false
})

const isMemberAssigned2 = computed(() => {
	if (props.detail?.userInfo?.memberTypes?.length) {
		return props.detail.userInfo.memberTypes.includes(MemberTypes.Assigned2)
	}
	return false
})

const isShowMemberBadges = computed(() => {
	return (
		isMemberOfficial1.value ||
		isMemberAssigned1.value ||
		isMemberAssigned2.value
	)
})

const memberTitle = computed(() => {
	if (isMemberOfficial1.value) {
		return props.text?.memberTypes.official1
	}
	if (isMemberAssigned1.value) {
		return props.text?.memberTypes.assigned1
	}
	if (isMemberAssigned2.value) {
		return props.text?.memberTypes.assigned2
	}
	return ''
})

const currentFromPostId = computed(() => {
	const fromPostId = new URLSearchParams(window.location.search).get(
		'fromPostId',
	)
	if (fromPostId) {
		return fromPostId
	}

	if (fromPostIdGlobal.value) {
		return fromPostIdGlobal.value
	}

	return ''
})

const linkRefund = computed(() => {
	return `${getSiteBaseUrl()}/protection/shopping/`
})

const imgAltText = computed(() => {
	return isSupportExpressCheckout.value ? props.detail.itemInfo.ecName : 'Image'
})

const jsonLdFormat = computed(() => {
	if (!props.detail || props.isLoading || !Object.keys(props.detail).length) {
		return ''
	}

	const { itemInfo, userInfo, id, title } = props.detail

	const productName =
		isSupportExpressCheckout.value && itemInfo.ecName
			? itemInfo.ecName
			: itemInfo.name

	const productImages =
		postImageList.value.map((item: any) => item.content) || []

	const price = props.priceLocalPost?.replace(/[^\d.]/g, '') || ''
	const currency =
		window.BUYANDSHIP.settings.current_country2.currency?.toUpperCase() || ''

	const shippingOrigin =
		props.warehouses?.[itemInfo.warehouse]?.alias.toUpperCase() || ''
	const shippingDestination = itemInfo.location?.toUpperCase() || ''

	const jsonLd = {
		'@context': 'https://schema.org',
		'@type': 'Product',
		name: productName,
		category: categoryName.value,
		image: productImages,
		offers: {
			'@type': 'Offer',
			price: Number(price).toString(),
			priceCurrency: currency,
			url: getProductSharingUrl('post', { id }),
			...(itemInfo.weight && {
				shippingDetails: {
					'@type': 'OfferShippingDetails',
					shippingOrigin: {
						'@type': 'DefinedRegion',
						addressCountry: shippingOrigin,
					},
					shippingDestination: {
						'@type': 'DefinedRegion',
						addressCountry: shippingDestination,
					},
				},
			}),
		},
		additionalProperty: [
			{
				'@type': 'PropertyValue',
				name: 'User Experience',
				value: `${title} - ${postContent.value}`,
				propertyID: `UserGenerated:${userInfo.name}`,
			},
			...(itemInfo.discounted &&
			itemInfo.discounted !== 'discount_not_available'
				? [
						{
							'@type': 'PropertyValue',
							name: 'Saved Percentage',
							value: itemInfo.discounted.toString(),
						},
				  ]
				: []),
		].filter(Boolean),
		mainEntityOfPage: {
			'@type': 'WebPage',
			'@id': getProductSharingUrl('post', { id }),
		},
	}

	try {
		return JSON.stringify(jsonLd)
	} catch (error) {
		console.error('JSON-LD transform fail:', error)
		return ''
	}
})

function action(name: string) {
	if (name === 'share') {
		shareMore()
		return
	}
	if (!productSharingData.isLogin && name !== 'share') {
		window.BsHeaderVm.showPanel('login')
		return
	}

	isHasPSProfile().then(isHasPSProfile => {
		if (!isHasPSProfile) {
			window.location.href = getProductSharingUrl('private-index')
			return
		}

		let prefix = ''
		let subfix = name
		if (name === 'like') {
			prefix = `${props.detail.isLiked ? '' : '/v2'}`
			if (props.detail.isLiked) {
				subfix = 'unlike'
			}
		} else if (name === 'bookmark') {
			prefix = `${props.detail.isBookmarked ? '' : '/v2'}`
			if (props.detail.isBookmarked) {
				subfix = 'unbookmark'
			}
		}
		const apiPath = `${prefix}/${subfix}`

		instanceProductSharing
			.post(apiPath, { post_id: props.detail.id })
			.then((res: any) => {
				const response = {
					isLiked: res.data.data.post.is_liked,
					isBookmarked: res.data.data.post.is_bookmarked,
					likeCount: res.data.data.post.like_count,
				}
				let payload
				if (name === 'like') {
					createGAEvent('ps_post_liked', {
						...eventParamsProductSlug.value,
						sequence_id: 'none',
					})
					sendEvent('like')
					sendEventActive()
					payload = {
						id: props.detail.id,
						param: 'isLiked',
						val: response.isLiked,
						likeCount: response.likeCount,
					}
				}
				if (name === 'bookmark') {
					createGAEvent('ps_post_bookmarked', eventParamsProductSlug.value)
					sendEvent('bookmark')
					sendEventActive()
					payload = {
						id: props.detail.id,
						param: 'isBookmarked',
						val: response.isBookmarked,
					}
				}

				if (payload) {
					emit('update-post', payload)
					if (setPostListInfoProvide) {
						setPostListInfoProvide(payload)
					}
				}

				if (name === 'report' || name === 'share') {
					const successMsg = props.text?.msg[name].success || ''
					addMessage(successMsg)
				}
				if (name === 'report') {
					createGAEvent(GaGeneralEvent.ButtonClick, {
						page_name: GaGeneralPageName.CommunityPostDetailPage,
						button_name: 'Report This Post',
						page_object_id: `post_${props.detail.id}`,
						post_id: props.detail.id,
						product_id: props.detail.productSlug,
						is_express_checkout: isSupportExpressCheckout.value,
					})
					sendEvent('report')
				}
			})
			.catch(() => {
				addMessage(props.text?.msg[name].fail)
			})
	})
}

function shareMore() {
	createGAEvent('ps_post_shared', {
		// duplicated column should be removed in the future
		category: props.detail.productSlug || 'none',
		product_slug: props.detail.productSlug || 'none',
		label: props.detail.id,
		post_id: props.detail.id,
		value: currentFromPostId.value,
		from_post_id: currentFromPostId.value,
	})
	sendEventActive()
	if (isShowDesktopLayout.value) {
		navigator.clipboard
			.writeText(shareCopyRef.value?.dataset.clipboardText || '')
			.then(() => addMessage(props.text?.msg.copy.success))
			.catch(() => addMessage(props.text?.msg.copy.failed))
	} else if (navigator.share) {
		navigator
			.share({
				title: 'Link for post',
				url: getProductSharingUrl('post', {
					id: props.detail.id,
					fromPostId: currentFromPostId.value,
				}),
			})
			.then(() => console.log('Successful share'))
			.catch(error => console.log('Error sharing', error))
	}
}

function viewUser() {
	sendEvent('author', {
		category: 'product_sharing',
		label: props.detail?.userInfo.name,
	})
	window.location.href = getProductSharingUrl('profile', {
		name: props.detail?.userInfo.name,
		fromPostId: props.detail.id,
	})
}

function getUrlQueryTransform(queryObj: any) {
	const urlParam: any[] = []

	Object.keys(queryObj).forEach(key => {
		urlParam.push(`${key}=${encodeURIComponent(queryObj[key])}`)
	})

	return urlParam
}

function getPosterImage(userImageObj: any) {
	if (
		!userImageObj ||
		!Object.keys(userImageObj).length ||
		!userImageObj.content
	)
		return null
	if (!userImageObj.has_resize) return userImageObj.content
	return userImageObj[ProductSharingImageSize.Profile]
}

function getMedia(mediaObj: any) {
	if (!mediaObj || !Object.keys(mediaObj).length || !mediaObj.content)
		return null
	if (mediaObj.type === 'ps_video' || !mediaObj.has_resize)
		return mediaObj.content
	return isShowDesktopLayout.value
		? mediaObj[ProductSharingImageSize.PostDesktop]
		: mediaObj[ProductSharingImageSize.PostMobile]
}

function getDateDifferenceValue(createTime: number) {
	return getTimeAgoValue(createTime, (props.text as any).timeago)
}

function sendEvent(
	name: keyof (typeof eventInfo)['postDetail'],
	params = {
		category: 'product_sharing',
		label: props.detail.id,
	},
) {
	const prefix = 'postDetail'
	if (eventInfo[prefix][name] === undefined) return
	createGAEvent(eventInfo[prefix][name], params)
}

function openProductUrl(isServiceAction = false) {
	createGAEvent(eventInfo.postDetail.openProductUrl, {
		// duplicated column should be removed in the future
		category: props.detail.productSlug || 'none',
		product_slug: props.detail.productSlug || 'none',
		label: props.detail.id,
		post_id: props.detail.id,
		value: isServiceAction ? 'primary_button' : 'product_info',
		from: isServiceAction ? 'primary_button' : 'product_info',
		from_post_id: currentFromPostId.value,
	})
	window.open(props.detail.itemInfo.url, '_blank')
}

function showDrawerRestrictions() {
	isShowDrawerRestrictions.value = true
}

function interceptInAppRequest() {
	if (isInAppBrowser()) {
		if (isSupportExpressCheckout.value) {
			openAppWithCheckout()
		} else {
			openApp()
		}
		return true
	}

	return false
}

function postServiceAction() {
	if (interceptInAppRequest()) {
		return
	}
	if (isSupportExpressCheckout.value) {
		if (!checkIsNewCommunityPost()) {
			createGAEvent('c_ec_f_ps_post_detail', {
				// duplicated column should be removed in the future
				category: currentFromPostId.value,
				from_post_id: currentFromPostId.value,
				label: props.detail.id,
				post_id: props.detail.id,
				value: props.detail.productSlug || 'none',
				product_slug: props.detail.productSlug || 'none',
			})
		} else {
			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: GaGeneralPageName.CommunityPostDetailPage,
				button_name: 'Add To New Request',
				page_object_id: `post_${props.detail.id}`,
				post_id: props.detail.id,
				product_id: props.detail.productSlug,
				is_express_checkout: isSupportExpressCheckout.value,
			})
		}
		let fromPostIdQuery = ''
		if (currentFromPostId.value) {
			if (import.meta.env.PROD) {
				fromPostIdQuery = `?fromPostId=${currentFromPostId.value}`
			} else {
				fromPostIdQuery = `&fromPostId=${currentFromPostId.value}`
			}
		}
		const link = `${getProductSharingUrl('express-checkout', {
			id: props.detail.id,
		})}${fromPostIdQuery}`
		window.open(link, '_blank')
		return
	}
	if (isBuyforyouEnable.value) {
		if (!checkIsNewCommunityPost()) {
			createGAEvent(eventInfo.postDetail.openBuyforyou, {
				// duplicated column should be removed in the future
				category: 'product_sharing',
				label: props.detail.id,
				post_id: props.detail.id,
				value: currentFromPostId.value || '',
				from_post_id: currentFromPostId.value || '',
			})
		} else {
			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: GaGeneralPageName.CommunityPostDetailPage,
				button_name: 'Add To New Request',
				page_object_id: `post_${props.detail.id}`,
				post_id: props.detail.id,
				product_id: props.detail.productSlug,
				is_express_checkout: isSupportExpressCheckout.value,
			})
		}
		if (!productSharingData.isLogin) {
			window.BsHeaderVm.showPanel('login')
			return
		}
		createGAEvent(eventInfo.postDetail.openBuyforyou, eventParams.value)
		const query = getUrlQueryTransform({
			default: 'true',
			warehouseId: props.detail.itemInfo.warehouse,
			itemName: props.detail.itemInfo.name,
			price: props.detail.itemInfo.unitPrice,
			link: props.detail.itemInfo.url,
			postId: props.detail.id,
		})
		const urlPrefix = `/buyforyou/new?${query.join('&')}`
		window.open(`${getMcrRootUrl()}${urlPrefix}`, '_blank')
		return
	}

	if (checkIsNewCommunityPost()) {
		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: GaGeneralPageName.CommunityPostDetailPage,
			button_name: 'Product Link - Unavailable',
			page_object_id: `post_${props.detail.id}`,
			post_id: props.detail.id,
			product_id: props.detail.productSlug,
			is_express_checkout: isSupportExpressCheckout.value,
		})
	}
	openProductUrl(true)
}

function handleBottomSecondaryButtonClick() {
	if (postService.value === 'bns') {
		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: GaGeneralPageName.CommunityPostDetailPage,
			button_name: 'View Warehouse - Unavailable',
			page_object_id: `post_${props.detail.id}`,
			post_id: props.detail.id,
			product_id: props.detail.productSlug,
			is_express_checkout: isSupportExpressCheckout.value,
		})

		if (!productSharingData.isLogin) {
			window.BsHeaderVm.showPanel('login')
			return
		}

		if (
			Object.keys(props.warehouses).includes(props.detail.itemInfo.warehouse)
		) {
			const warehouseAlias =
				props.warehouses?.[props.detail.itemInfo.warehouse]?.alias
			window.open(
				`${getMcrRootUrl()}/warehouse/detail/${warehouseAlias}`,
				'_blank',
			)
		} else {
			window.open(`${getMcrRootUrl()}/warehouse`, '_blank')
		}
	} else if (postService.value === 'b4u') {
		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: GaGeneralPageName.CommunityPostDetailPage,
			button_name: 'Product Link',
			page_object_id: `post_${props.detail.id}`,
			post_id: props.detail.id,
			product_id: props.detail.productSlug,
			is_express_checkout: isSupportExpressCheckout.value,
		})

		openProductUrl(true)
	}
}

function goBack() {
	window.history.back()
}

function getPricePost(isItemInfo = false) {
	const price =
		isSupportExpressCheckout.value && !isItemInfo
			? props.detail.itemInfo.ecPrice
			: props.detail.itemInfo.unitPrice
	return `${currencySymbol.value} ${Number(price).toLocaleString('en')}`
}

function sendEventActive() {
	createGAEvent('ps_active_user')
}

function setIsRecommendShow(value: boolean) {
	isRecommendShow.value = value
}

function setInfoBox() {
	currentInfoBoxContent.value = getInfoBoxContent({
		fulfillmentMode: props.detail.ecFulfillmentMode,
		fulfillmentRange: props.detail.ecFulfillmentRange,
		fulfillmentMonth: props.detail.ecFulfillmentMonth,
		fulfillmentMaxOrderPerUser: props.detail.ecFulfillmentMaxOrderPerUser,
		fulfillmentMaxQuantity: props.detail.ecFulfillmentMaxQuantity,
		isOutOfStock: false,
		text: props.text,
	})
}

function handlePurchaseUnavailableAttentionBoxClick() {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.CommunityPostDetailPage,
		button_name: 'Learn More - Unavailable',
		page_object_id: `post_${props.detail.id}`,
		post_id: props.detail.id,
		product_id: props.detail.productSlug,
		is_express_checkout: isSupportExpressCheckout.value,
	})
	window.location.href = `${getSiteBaseUrl()}/features/shipping/`
}

function hideShippingHowItWork() {
	isShowShippingHowItWork.value = false
}

function showShippingHowItWork() {
	isShowShippingHowItWork.value = true
}

const tooltipContent = computed(() => {
	return `<div class="flex gap-8px flex-col py-[9px] px-[4px]">
		<div class="mb-8px typography-s2-emp text-white">${props.text?.newUI.priceTooltipTitle}</div>
		<div class="typography-b3-reg text-white">${props.text?.newUI.priceTooltipDescription}</div>
	</div>`
})

async function fetchProductSharingSettings() {
	try {
		const data = await fetchProductSharingSettingsApi()
		isAcceptReturnRefund.value = data.isAcceptReturnRefund
	} catch (error) {
		console.log(error)
	}
}

watch(
	() => isShowDrawerRestrictions.value,
	value => {
		if (value) {
			isWebComponentLoading.value = true
			const webcomponent: any = document.createElement(
				'web-component-post-reminder-how-it-works-view',
			)
			if (webcomponent.promise === undefined) {
				return
			}

			webcomponent.promise.then((title: string) => {
				isWebComponentLoading.value = false
				sideDrawerTitleRestrictions.value = title
			})
		}
	},
)

watch(
	() => isShowShippingHowItWork.value,
	value => {
		if (value) {
			isShippingWebComponentLoading.value = true
			const webcomponent: any = document.createElement(
				WebComponentNameList.CheckoutShippingHowItWorksView,
			)
			if (webcomponent.promise === undefined) {
				return
			}

			webcomponent.promise.then((title: string) => {
				isShippingWebComponentLoading.value = false
				shippingHowItWorkTitle.value = title
			})
		}
	},
)

watch(
	() => props.isLoading,
	value => {
		if (!value) {
			setInfoBox()
			if (
				!window.BUYANDSHIP.settings.user_onboarded &&
				postService.value !== 'easybuy'
			) {
				window.BsHeaderVm.setTypeSuccessDialog(postService.value)
			}
			if (
				window.BsHeaderVm.isNeedTriggerActionButton &&
				postService.value !== 'easybuy'
			) {
				postServiceAction()
			}
		}
	},
)

fetchProductSharingSettings()

onMounted(() => {
	createPostReminderHowItWorksView()
	createCheckoutShippingHowItWorksView()
})
</script>

<template>
	<div v-if="!checkIsNewCommunityPost()">
		<div
			:class="[
				{ 'bs-product-sharing-post-card': !isShowContentBlock },
				{
					'bs-product-sharing-post-card--max':
						!isShowContentBlock && isShowDesktopLayout && isTypeModal,
				},
			]"
		>
			<BsLoader v-if="isLoading"></BsLoader>
			<ProductSharingEmptyContent
				v-if="!isLoading && isShowContentBlock"
				:text="text"
			></ProductSharingEmptyContent>
			<div
				class="bs-product-sharing-post-card__sticky-wrapper"
				:class="{
					'bs-product-sharing-post-card__sticky-wrapper--padding': !isTypeModal,
				}"
			>
				<template
					v-if="
						!isLoading && !isShowContentBlock && detail && isShowDesktopLayout
					"
				>
					<div class="bs-product-sharing-post-card__main">
						<BsGallerySlideshow
							:mediaList="postImageList"
							:textClose="text?.gallery.close"
							:imgAltText="imgAltText"
						/>
						<div class="bs-product-sharing-post-card__action">
							<span class="bs-product-sharing-post-card__action__item">
								<BsButtonNormal
									v-if="likeCountDisplay !== '0'"
									:theme="ButtonNormalTheme.Social"
									:text="likeCountDisplay"
									:action="() => action('like')"
								>
									<template #leading>
										<span class="bs-product-sharing-post-card__action__item">
											<i
												:class="[
													'bs-icon',
													detail.isLiked
														? 'bs-icon--like--fill'
														: 'bs-icon--like',
												]"
											></i>
										</span>
									</template>
								</BsButtonNormal>
								<BsButtonNormal
									v-else
									:text="''"
									:theme="ButtonNormalTheme.Social"
									:action="() => action('like')"
								>
									<template #leading>
										<span class="bs-product-sharing-post-card__action__item">
											<i
												:class="[
													'bs-icon',
													detail.isLiked
														? 'bs-icon--like--fill'
														: 'bs-icon--like',
												]"
											></i>
										</span>
									</template>
								</BsButtonNormal>
							</span>
							<span class="bs-product-sharing-post-card__action__item">
								<BsButtonNormal
									:theme="ButtonNormalTheme.Social"
									:text="text?.button.share"
									:action="() => action('share')"
								>
									<template v-slot:leading>
										<span class="bs-product-sharing-post-card__action__item">
											<i
												class="bs-icon bs-icon--share"
												ref="shareCopyRef"
												:data-clipboard-text="
													getProductSharingUrl('share-post', { id: detail.id })
												"
											></i>
										</span>
									</template>
								</BsButtonNormal>
								<BsButtonNormal
									:theme="ButtonNormalTheme.Social"
									:text="text?.button.bookmark"
									:action="() => action('bookmark')"
								>
									<template #leading>
										<span class="bs-product-sharing-post-card__action__item">
											<i
												:class="[
													'bs-icon',
													detail.isBookmarked
														? 'bs-icon--bookmark--fill'
														: 'bs-icon--bookmark',
												]"
											></i>
										</span>
									</template>
								</BsButtonNormal>
							</span>
						</div>
						<div
							class="bs-product-sharing-post-card__info bs-product-sharing-post-card__info__post"
							v-if="!isLoading"
						>
							<div
								@click="viewUser"
								class="bs-product-sharing-post-card__poster"
							>
								<div class="bs-product-sharing-post-card__poster__avatar">
									<img
										class="bs-product-sharing-post-card__poster__avatar__img"
										:src="posterAvatar"
										alt="Avatar"
										loading="lazy"
									/>
								</div>
								<div class="bs-product-sharing-post-card__poster__info">
									<div class="bs-product-sharing-post-card__poster__info__name">
										<div
											class="bs-product-sharing-post-card__poster__info__name--text"
										>
											{{ detail.userInfo.name }}
										</div>
										<div
											class="bs-product-sharing-post-card__poster__info__name--member"
											v-if="isShowMemberBadges"
										>
											<span
												class="bs-icon-member-official-1"
												v-if="isMemberOfficial1"
											></span>
											<span
												class="bs-icon-member-assigned-1"
												v-if="isMemberAssigned1"
											></span>
											<span
												class="bs-icon-member-assigned-2"
												v-if="isMemberAssigned2"
											></span>
											<div
												class="bs-product-sharing-post-card__poster__info__name--member__title"
												v-if="memberTitle"
											>
												{{ memberTitle }}
											</div>
										</div>
									</div>
									<div
										class="bs-product-sharing-post-card__poster__info__other"
									>
										{{ getDateDifferenceValue(detail.publishDate) }}
									</div>
								</div>
							</div>
							<div class="bs-product-sharing-post-card__post">
								<div v-if="!isSupportExpressCheckout">
									<h1 class="bs-product-sharing-post-card__post__title">
										{{ detail.title }}
									</h1>
								</div>
								<div v-else>
									<div class="bs-product-sharing-post-card__post__title">
										{{ detail.title }}
									</div>
								</div>

								<div class="bs-product-sharing-post-card__post__content">
									<div
										class="bs-product-sharing-post-card__post__content__text"
										v-html="postContent"
									></div>
								</div>
							</div>
						</div>
						<div
							v-for="(info, key) in infoDisplay"
							:key="key"
							class="bs-product-sharing-post-card__info"
						>
							<h2 class="bs-product-sharing-post-card__info__title">
								{{ text?.title[`${key}`] }}
								<BsButtonOutline
									v-if="(key as any) === 'product'"
									:text="text?.button.openItemUrl"
									:action="openProductUrl"
								>
									<template #trailing>
										<span class="bs-product-sharing-post-card__action__item">
											<i class="bs-icon bs-icon--share-open"></i>
										</span>
									</template>
								</BsButtonOutline>
							</h2>
							<div
								v-for="item in info.value"
								:key="item.icon"
								class="bs-product-sharing-post-card__info__item"
							>
								<i :class="`bs-icon bs-icon--${item.icon}`"></i>
								<div class="bs-product-sharing-post-card__info__item__content">
									<h3
										class="bs-product-sharing-post-card__info__item__content__title"
									>
										{{ item.title }}
									</h3>
									<span
										class="bs-product-sharing-post-card__info__item__content__value"
									>
										{{ item.value }}
									</span>
								</div>
							</div>
						</div>
						<div class="bs-product-sharing-post-card__bottom">
							<div
								class="bs-product-sharing-post-card__bottom__report"
								@click="action('report')"
							>
								<i class="bs-icon bs-icon--report"></i>
								<span
									class="bs-product-sharing-post-card__bottom__report__text"
								>
									{{ text?.button.report }}
								</span>
							</div>
							<a
								class="bs-product-sharing-post-card__bottom__refund"
								v-if="isAcceptReturnRefund"
								:href="linkRefund"
								target="_blank"
							>
								<i class="bs-icon bs-icon--refund"></i>
								<span
									class="bs-product-sharing-post-card__bottom__refund__text"
								>
									{{ text?.button.refund }}
								</span>
							</a>
						</div>
					</div>
					<div
						v-if="!isLoading && detail"
						:class="[
							'bs-product-sharing-post-card__side',
							{ 'bs-product-sharing-post-card__side--padding': !isTypeModal },
						]"
					>
						<div
							v-if="isSupportExpressCheckout"
							class="bs-product-sharing-post-card__service"
						>
							<i :class="`bs-icon bs-icon--${postService}`"></i>
							<div class="bs-product-sharing-post-card__service__content">
								<span
									class="bs-product-sharing-post-card__service__content__title"
								>
									{{ text?.service.title[postService] }}
								</span>
							</div>
						</div>
						<div
							v-if="isSupportExpressCheckout"
							class="bs-product-sharing-post-card__product"
						>
							<h1 class="bs-product-sharing-post-card__product__name">
								{{
									isSupportExpressCheckout
										? detail.itemInfo.ecName
										: detail.itemInfo.name
								}}
							</h1>
							<div
								:class="[
									'bs-product-sharing-post-card__product__price',
									{ adjustment: isLocalCurrency },
								]"
							>
								{{ isLocalCurrency ? localPricing : getPricePost() }}
							</div>
							<div
								class="bs-product-sharing-post-card__product__price__local"
								v-if="!isLocalCurrency"
							>
								{{ priceLocal }}
							</div>
						</div>
						<div
							:class="[
								'bs-product-sharing-post-card__catch',
								{
									'bs-product-sharing-post-card__catch--reverse':
										!isSupportExpressCheckout,
								},
							]"
						>
							<!-- Be careful when change id name here, might affect AuthSuccess.vue -->
							<BsButtonNormal
								id="success-dialog-primary-button"
								class="bs-product-sharing-post-card__product__button"
								:isRoundEdge="false"
								:text="text?.service.action[postService]"
								:action="postServiceAction"
							>
								<template #trailing>
									<span
										v-if="!isSupportExpressCheckout"
										class="bs-product-sharing-post-card__action__item"
									>
										<i class="bs-icon bs-icon--share-open"></i>
									</span>
								</template>
							</BsButtonNormal>
							<BsNoticeBlock
								v-if="isSupportExpressCheckout && currentInfoBoxContent?.title"
								class="bs-product-sharing__notice-block desktop"
								:icon="NoticeBlockIconType.Info"
							>
								<template v-slot:title>
									{{ currentInfoBoxContent.title }}
								</template>
								<template v-slot:description>
									<div v-html="currentInfoBoxContent.description"></div>
								</template>
							</BsNoticeBlock>
							<div class="bs-product-sharing-post-card__promote">
								<div class="bs-product-sharing-post-card__promote__title">
									{{ text?.promote.title[postService] }}
								</div>
								<div class="bs-product-sharing-post-card__promote__desc">
									{{ text?.promote.content[postService] }}
								</div>
							</div>
						</div>
						<div v-if="isDifferentCountry" class="bs-info-section--container">
							<BsInfoSection
								:theme="infoSectionData.theme"
								:content="infoSectionData.text"
								:arrowText="infoSectionData.arrowText"
								:action="showDrawerRestrictions"
							/>
						</div>
					</div>
				</template>
				<template
					v-if="
						!isLoading && !isShowContentBlock && detail && !isShowDesktopLayout
					"
				>
					<div
						:class="[
							'bs-product-sharing-post-card__nav',
							{ 'bs-product-sharing-post-card__nav--between': isTypeModal },
						]"
					>
						<span v-if="isTypeModal" @click="goBack">
							<i class="bs-icon bs-icon--back--circle"></i>
						</span>
						<div class="bs-product-sharing-post-card__action__item">
							<span class="bs-member-icon-button" @click="action('share')">
								<i
									class="bs-icon bs-icon--share--circle"
									ref="shareCopyRef"
									:data-clipboard-text="
										getProductSharingUrl('share-post', { id: detail.id })
									"
								></i>
							</span>
							<span class="bs-member-icon-button" @click="action('bookmark')">
								<i
									:class="[
										'bs-icon',
										detail.isBookmarked
											? 'bs-icon--bookmark--circle--fill'
											: 'bs-icon--bookmark--circle',
									]"
								></i>
							</span>
						</div>
					</div>
					<BsGallerySlideshow
						:mediaList="postImageList"
						:textClose="text?.gallery.close"
						:imgAltText="imgAltText"
					/>
					<div v-if="detail" class="bs-product-sharing-post-card__main">
						<div
							v-if="isSupportExpressCheckout"
							class="bs-product-sharing-post-card__service"
						>
							<i :class="`bs-icon bs-icon--${postService}`"></i>
							<div class="bs-product-sharing-post-card__service__content">
								<span
									class="bs-product-sharing-post-card__service__content__title"
								>
									{{ text?.service.title[postService] }}
								</span>
							</div>
						</div>
						<h1
							v-if="isSupportExpressCheckout"
							class="bs-product-sharing-post-card__product__name"
						>
							{{ detail.itemInfo.ecName }}
						</h1>
						<BsNoticeBlock
							v-if="isSupportExpressCheckout && currentInfoBoxContent?.title"
							class="bs-product-sharing__notice-block mobile"
							:icon="NoticeBlockIconType.Info"
						>
							<template v-slot:title>
								{{ currentInfoBoxContent.title }}
							</template>
							<template v-slot:description>
								<div v-html="currentInfoBoxContent.description"></div>
							</template>
						</BsNoticeBlock>
						<div
							class="bs-product-sharing-post-card__info bs-product-sharing-post-card__info__post"
							v-if="!isLoading"
						>
							<div
								@click="viewUser"
								class="bs-product-sharing-post-card__poster"
							>
								<div class="bs-product-sharing-post-card__poster__avatar">
									<img
										class="bs-product-sharing-post-card__poster__avatar__img"
										:src="posterAvatar"
										alt="avatar"
										loading="lazy"
									/>
								</div>
								<div class="bs-product-sharing-post-card__poster__info">
									<div class="bs-product-sharing-post-card__poster__info__name">
										<div
											class="bs-product-sharing-post-card__poster__info__name--text"
										>
											{{ detail.userInfo.name }}
										</div>
										<div
											class="bs-product-sharing-post-card__poster__info__name--member"
											v-if="isShowMemberBadges"
										>
											<span
												class="bs-icon-member-official-1"
												v-if="isMemberOfficial1"
											></span>
											<span
												class="bs-icon-member-assigned-1"
												v-if="isMemberAssigned1"
											></span>
											<span
												class="bs-icon-member-assigned-2"
												v-if="isMemberAssigned2"
											></span>
										</div>
									</div>
									<div
										class="bs-product-sharing-post-card__poster__info__other"
									>
										{{ getDateDifferenceValue(detail.publishDate) }}
									</div>
								</div>
							</div>
							<div class="bs-product-sharing-post-card__post">
								<div v-if="!isSupportExpressCheckout">
									<h1 class="bs-product-sharing-post-card__post__title">
										{{ detail.title }}
									</h1>
								</div>
								<div v-else>
									<div class="bs-product-sharing-post-card__post__title">
										{{ detail.title }}
									</div>
								</div>

								<div class="bs-product-sharing-post-card__post__content">
									<div
										class="bs-product-sharing-post-card__post__content__text"
										v-html="postContent"
									></div>
								</div>
							</div>
						</div>
						<div class="bs-product-sharing-post-card__action">
							<span class="bs-product-sharing-post-card__action__item">
								<BsButtonNormal
									v-if="likeCountDisplay !== '0'"
									:theme="ButtonNormalTheme.Social"
									:text="likeCountDisplay"
									:action="() => action('like')"
								>
									<template #leading>
										<span class="bs-product-sharing-post-card__action__item">
											<i
												:class="[
													'bs-icon',
													detail.isLiked
														? 'bs-icon--like--fill'
														: 'bs-icon--like',
												]"
											></i>
										</span>
									</template>
								</BsButtonNormal>
								<BsButtonNormal
									v-else
									:theme="ButtonNormalTheme.Social"
									:text="''"
									:action="() => action('like')"
								>
									<template #leading>
										<span class="bs-product-sharing-post-card__action__item">
											<i
												:class="[
													'bs-icon',
													detail.isLiked
														? 'bs-icon--like--fill'
														: 'bs-icon--like',
												]"
											></i>
										</span>
									</template>
								</BsButtonNormal>
							</span>
						</div>
						<div v-for="(info, key) in infoDisplay" :key="key">
							<div class="bs-product-sharing-post-card__info">
								<h2 class="bs-product-sharing-post-card__info__title">
									{{ text?.title[`${key}`] }}
								</h2>
								<div
									v-for="item in info.value"
									:key="item.icon"
									class="bs-product-sharing-post-card__info__item"
								>
									<i :class="`bs-icon bs-icon--${item.icon}`"></i>
									<div
										class="bs-product-sharing-post-card__info__item__content"
									>
										<h3
											class="bs-product-sharing-post-card__info__item__content__title"
										>
											{{ item.title }}
										</h3>
										<span
											class="bs-product-sharing-post-card__info__item__content__value"
										>
											{{ item.value }}
										</span>
									</div>
								</div>
								<BsButtonOutline
									v-if="(key as any) === 'product'"
									:text="text?.button.openItemUrl"
									:action="openProductUrl"
								>
									<template #trailing>
										<BsIconButton
											class="bs-product-sharing-post-card__action__item"
											:size="20"
										>
											<i class="bs-icon bs-icon--share-open"></i>
										</BsIconButton>
									</template>
								</BsButtonOutline>
							</div>
							<div
								v-if="(key as any) === 'product' && isDifferentCountry"
								class="bs-info-section--container"
							>
								<BsInfoSection
									:theme="infoSectionData.theme"
									:content="infoSectionData.text"
									:arrowText="infoSectionData.arrowText"
									:action="showDrawerRestrictions"
								/>
							</div>
						</div>
						<div class="bs-product-sharing-post-card__bottom">
							<div
								class="bs-product-sharing-post-card__bottom__report"
								@click="action('report')"
							>
								<i class="bs-icon bs-icon--report"></i>
								<span
									class="bs-product-sharing-post-card__bottom__report__text"
								>
									{{ text?.button.report }}
								</span>
							</div>
							<a
								class="bs-product-sharing-post-card__bottom__refund"
								v-if="isAcceptReturnRefund"
								:href="linkRefund"
								target="_blank"
							>
								<i class="bs-icon bs-icon--refund"></i>
								<span
									class="bs-product-sharing-post-card__bottom__refund__text"
								>
									{{ text?.button.refund }}
								</span>
							</a>
						</div>
						<template v-if="!isLoading && isRecommendShow">
							<div class="bs-product-sharing-post-card__recommend">
								<div class="bs-product-sharing-post-card__recommend__title">
									{{ text?.recommend.title }}
								</div>
								<ProductSharingPostList
									:text="postListTextInfo"
									:isRecommend="true"
									:columnCount="2"
									:currentPostId="detail.id"
									:postPerPage="productSharingPostPerPage.recommend"
									:isTypeModal="isTypeModal"
									@setIsRecommendShow="setIsRecommendShow"
								></ProductSharingPostList>
							</div>
						</template>
					</div>
					<BsStickyBottomContainer class="bs-product-sharing-post-card__sticky">
						<div
							v-if="postService === 'easybuy'"
							class="bs-product-sharing-post-card__product"
						>
							<div class="bs-product-sharing-post-card__product__price">
								{{ isLocalCurrency ? localPricing : getPricePost() }}
							</div>
							<div
								class="bs-product-sharing-post-card__product__price__local"
								v-if="!isLocalCurrency"
							>
								{{ priceLocal }}
							</div>
						</div>
						<!-- Be careful when change id name here, might affect AuthSuccess.vue -->
						<BsButtonNormal
							id="success-dialog-primary-button"
							class="bs-product-sharing-post-card__product__button"
							:isRoundEdge="false"
							:text="text?.service.action[postService]"
							:action="postServiceAction"
						>
							<template #trailing>
								<span
									v-if="!isSupportExpressCheckout"
									class="bs-product-sharing-post-card__action__item"
								>
									<i class="bs-icon bs-icon--share-open"></i>
								</span>
							</template>
						</BsButtonNormal>
					</BsStickyBottomContainer>
				</template>
			</div>
			<template
				v-if="
					!isLoading &&
					!isShowContentBlock &&
					isShowDesktopLayout &&
					isRecommendShow
				"
			>
				<div class="bs-product-sharing-post-card__recommend desktop">
					<div class="bs-product-sharing-post-card__recommend__title">
						{{ text?.recommend.title }}
					</div>
					<ProductSharingPostList
						:text="postListTextInfo"
						:isRecommend="true"
						:currentPostId="detail.id"
						:columnCount="5"
						:postPerPage="productSharingPostPerPage.recommend"
						:isTypeModal="isTypeModal"
						@setIsRecommendShow="setIsRecommendShow"
					></ProductSharingPostList>
				</div>
			</template>
			<Teleport to="body">
				<BsSideDrawer
					v-model="isShowDrawerRestrictions"
					:title="sideDrawerTitleRestrictions"
					:isShowTrailing="true"
				>
					<BsHowItWorksSkeleton v-if="isWebComponentLoading" />
					<div v-show="!isWebComponentLoading">
						<web-component-post-reminder-how-it-works-view v-pre />
					</div>
				</BsSideDrawer>
			</Teleport>
		</div>
	</div>

	<div v-else>
		<DefinePostAuthorContent>
			<div class="flex flex-col gap-16px">
				<div class="flex cursor-pointer gap-16px" @click="viewUser">
					<img
						class="h-32px w-32px rounded-full object-cover mdp:h-48px mdp:w-48px"
						:src="posterAvatar"
						alt="Avatar"
						loading="lazy"
					/>

					<div class="flex flex-col gap-4px">
						<div class="typography-s2-emp text-n-950">
							{{ detail.userInfo.name }}
						</div>

						<div class="typography-c2-reg text-n-500">
							{{ getDateDifferenceValue(detail.publishDate) }}
						</div>
					</div>
				</div>

				<div class="typography-s1-emp text-black">{{ detail.title }}</div>

				<div
					class="typography-b3-reg whitespace-pre-wrap text-black"
					v-html="postContent"
				></div>
			</div>
		</DefinePostAuthorContent>

		<DefinePostDetailContent>
			<div
				class="grid grid-cols-1 gap-16px mdl:grid-cols-2 mdl:gap-x-[40px] mdl:gap-y-24px lg:gap-x-[64px]"
			>
				<ReuseInfoItem
					:iconName="'community_post_weight'"
					:title="text?.newUI.detailTitles.weight"
					:value="
						shippingInfo.find(item => item.icon === 'weight')?.value || ''
					"
				/>

				<ReuseInfoItem
					:iconName="'community_post_warehouse'"
					:title="text?.newUI.detailTitles.warehouse"
					:value="
						shippingInfo.find(item => item.icon === 'warehouse')?.value || ''
					"
				/>

				<ReuseInfoItem
					v-if="
						text?.paymentMethod[detail.itemInfo.paymentMethod] &&
						postService !== 'easybuy'
					"
					:iconName="'community_post_payment'"
					:title="text?.newUI.detailTitles.payment"
					:value="
						productInfo.find(item => item.icon === 'credit-card')?.value || ''
					"
				/>

				<ReuseInfoItem
					:iconName="'plane_v3'"
					:title="text?.newUI.detailTitles.delivery"
					:value="
						shippingInfo.find(item => item.icon === 'delivery')?.value || ''
					"
				/>
			</div>
		</DefinePostDetailContent>

		<DefineInfoItem v-slot="{ iconName, title, value }">
			<div class="flex gap-16px">
				<BaseIcon :name="iconName" class="h-24px w-24px shrink-0" />

				<div class="flex flex-col gap-[2px]">
					<span class="typography-c2-str text-[#5A5A5A]">
						{{ title }}
					</span>
					<span class="typography-s2-emp text-n-800">
						{{ value }}
					</span>
				</div>
			</div>
		</DefineInfoItem>

		<DefinePostBottomContent>
			<div class="flex flex-col gap-16px mdp:flex-row mdp:gap-[50px]">
				<ReuseInfoItem
					:iconName="'app_info_display'"
					:title="text?.newUI.detailTitles.category"
					:value="interestName"
				/>

				<div
					class="flex cursor-pointer items-center gap-16px"
					@click="action('report')"
				>
					<BaseIcon name="report" class="h-16px w-16px fill-n-950" />

					<span class="typography-s2-reg text-n-950 underline">
						{{ text?.button.report }}
					</span>
				</div>
			</div>
		</DefinePostBottomContent>

		<DefineAboutShoppingContent v-slot="{ shoppingItems }">
			<div class="flex flex-col gap-good-friends">
				<div class="typography-h5-str text-n-950">
					{{ text?.newUI.shoppingSectionTitle }}
				</div>
				<div class="rounded-[8px] bg-n-50 p-16px">
					<div
						class="cursor-pointer"
						v-for="(item, index) in shoppingItems"
						:key="item.id"
						@click="item.action"
					>
						<div class="flex items-center gap-16px">
							<BaseIcon :name="item.icon" class="h-24px w-24px fill-n-950" />
							<div
								class="grow text-[0.875rem] font-[500] leading-[0.02188rem] text-n-950"
							>
								{{ item.title }}
							</div>
							<BaseIcon
								name="arrow_right_full"
								class="h-24px w-24px fill-n-950"
							/>
						</div>

						<div
							class="my-16px h-[1px] w-full bg-n-100"
							:class="{ hidden: index === shoppingItems.length - 1 }"
						></div>
					</div>
				</div>
			</div>
		</DefineAboutShoppingContent>

		<div
			:class="[
				{ 'bs-product-sharing-post-card': !isShowContentBlock },
				{
					'bs-product-sharing-post-card--max':
						!isShowContentBlock && isShowDesktopLayout && isTypeModal,
				},
				'lg:mx-auto lg:max-w-[1200px] lg:px-0',
			]"
		>
			<BsLoader v-if="isLoading"></BsLoader>
			<ProductSharingEmptyContent
				v-if="!isLoading && isShowContentBlock"
				:text="text"
			></ProductSharingEmptyContent>
			<div
				class="bs-product-sharing-post-card__sticky-wrapper"
				:class="{
					'bs-product-sharing-post-card__sticky-wrapper--padding': !isTypeModal,
				}"
			>
				<template
					v-if="
						!isLoading && !isShowContentBlock && detail && isShowDesktopLayout
					"
				>
					<div
						class="bs-product-sharing-post-card__main mdp:max-w-[50%] mdl:!mr-[64px] mdl:w-[620px] mdl:max-w-[620px]"
					>
						<BsGallerySlideshow
							:mediaList="postImageList"
							:textClose="text?.gallery.close"
							:imgAltText="imgAltText"
						/>
						<div class="bs-product-sharing-post-card__action !mb-24px">
							<span class="bs-product-sharing-post-card__action__item">
								<div
									class="flex cursor-pointer items-center gap-8px border border-n-100 bg-white p-[10px]"
									:class="[
										likeCountDisplay !== '0'
											? 'rounded-[50px]'
											: 'rounded-full',
									]"
									@click="() => action('like')"
								>
									<BaseIcon
										:name="detail.isLiked ? 'like_filled' : 'like_hollow'"
										class="h-16px w-16px"
										:class="[detail.isLiked ? 'fill-[#FF5C00]' : 'fill-n-950']"
									/>
									<span
										v-if="likeCountDisplay !== '0'"
										class="typography-l2-emp text-[14px] text-n-950"
									>
										{{ likeCountDisplay }}
									</span>
								</div>
							</span>
							<span class="bs-product-sharing-post-card__action__item">
								<div
									class="mr-16px flex cursor-pointer items-center gap-8px border border-n-100 bg-white p-[10px]"
									:class="[
										likeCountDisplay !== '0'
											? 'rounded-[50px]'
											: 'rounded-full',
									]"
									@click="() => action('share')"
								>
									<div
										ref="shareCopyRef"
										:data-clipboard-text="
											getProductSharingUrl('share-post', { id: detail.id })
										"
									>
										<BaseIcon name="upload" class="h-16px w-16px fill-n-950" />
									</div>
								</div>

								<div
									class="flex cursor-pointer items-center gap-8px border border-n-100 bg-white p-[10px]"
									:class="[
										likeCountDisplay !== '0'
											? 'rounded-[50px]'
											: 'rounded-full',
									]"
									@click="() => action('bookmark')"
								>
									<BaseIcon
										:name="detail.isBookmarked ? 'save_filled' : 'save'"
										class="h-16px w-16px fill-n-950"
									/>
								</div>
							</span>
						</div>

						<div
							class="rounded-[8px] border border-n-100 bg-white px-[16px] py-[20px] mdp:p-24px"
							v-if="!isLoading"
						>
							<ReusePostAuthorContent />

							<div class="my-[24px] h-[1px] w-full bg-n-100"></div>

							<ReusePostBottomContent />
						</div>
					</div>
					<div v-if="!isLoading && detail" class="flex-1">
						<div
							v-if="isSupportExpressCheckout"
							class="mb-12px flex items-center gap-[10px]"
						>
							<div class="rounded-[4px] bg-[#FF760A] p-[3px]">
								<BaseIcon name="easybuy" class="h-[18px] w-[18px] fill-white" />
							</div>
							<span class="typography-s2-emp text-[#FF760A]">
								{{ text?.service.title.easybuy }}
							</span>
						</div>

						<div class="typography-h3-str mb-24px text-black">
							{{
								productInfo.find(item => item.icon === 'product')?.value || ''
							}}
						</div>

						<div
							class="mb-24px rounded-[8px] border border-n-100 bg-white px-24px py-good-friends"
						>
							<div
								v-if="postService !== 'easybuy'"
								class="mb-24px flex items-center"
							>
								<div class="typography-h5-str mr-8px text-n-950">
									{{ text?.newUI.priceDesktopTitle }}
								</div>
								<BaseIcon
									v-tooltip="{
										content: tooltipContent,
										placement: 'top',
										popperClass:
											'max-w-[300px] whitespace-pre-line bg-black rounded-lg',
										autoHide: true,
										distance: 8,
										triggers: ['click'],
										html: true,
									}"
									name="suggest-Info"
									class="fill-n950 h-24px w-24px cursor-pointer"
								/>
							</div>

							<div class="flex flex-col lg:flex-row">
								<div class="flex flex-col gap-4px">
									<div
										v-if="postService !== 'easybuy'"
										class="flex h-[28px] items-center gap-8px"
									>
										<div class="typography-c2-str text-[#5A5A5A]">
											{{ text?.newUI.buyerPaidPriceDesktop }}
										</div>

										<div v-if="detail.itemInfo.discounted">
											<BaseTag
												v-if="
													detail.itemInfo.discounted !==
													'discount_not_available'
												"
												variant="info"
												size="medium"
												class="flex items-center px-0 [&_.discounted]:text-[0.79363rem] [&_.discounted]:font-[700] [&_.discounted]:leading-[1.13375rem] [&_.discounted]:text-t-600"
												v-html="displayedDiscountFromReportedPriceDesktop"
											></BaseTag>
											<BaseTag
												v-else
												variant="info"
												size="medium"
												class="flex items-center px-0"
											>
												{{ text?.newUI.notAvailableOnLocal }}
											</BaseTag>
										</div>
									</div>

									<div class="typography-h2-str text-black">
										{{ priceLocalPost?.replace('≈ ', '') }}
									</div>
									<div class="typography-c1-reg text-[#818181]">
										{{ getPricePost(true) }}
									</div>
								</div>

								<template
									v-if="
										detail.itemInfo.discounted &&
										detail.itemInfo.discounted !== 'discount_not_available' &&
										postService !== 'easybuy'
									"
								>
									<div
										class="my-16px h-[1px] w-full bg-[#E3E3E3] lg:mx-32px lg:my-0 lg:h-auto lg:w-[1px]"
									></div>

									<div class="flex flex-col gap-4px">
										<div class="flex h-[28px] items-center gap-8px">
											<div class="typography-c2-str text-[#5A5A5A]">
												{{ text?.newUI.buyerReportedLocalPriceDesktop }}
											</div>
										</div>

										<div class="typography-h5-str text-[#818181]">
											{{ reportedLocalPrice }}
										</div>
										<div class="typography-c1-reg text-[#818181]">
											{{ reportedLocalPriceInOriginalCurrency }}
										</div>
									</div>
								</template>
							</div>
						</div>

						<div
							class="rounded-[8px] border border-n-100 bg-white px-24px py-good-friends"
						>
							<ReusePostDetailContent />
						</div>

						<div class="mt-24px flex flex-1 gap-16px">
							<!-- Be careful when change id name here, might affect AuthSuccess.vue -->
							<BaseButton
								class="w-full"
								id="success-dialog-primary-button"
								size="large"
								@click="postServiceAction"
							>
								<template #leadingIcon v-if="postService === 'b4u'">
									<BaseIcon name="cart_v3" class="h-16px w-16px fill-white" />
								</template>

								{{ text?.service.action[postService] }}
							</BaseButton>

							<BaseButton
								v-if="postService === 'b4u' || postService === 'bns'"
								class="w-full"
								variant="secondary"
								size="large"
								@click="handleBottomSecondaryButtonClick"
							>
								{{
									postService === 'b4u'
										? text?.newUI.buttons.viewSite
										: text?.newUI.buttons.viewWarehouse
								}}
							</BaseButton>
						</div>
						<BsNoticeBlock
							v-if="isSupportExpressCheckout && currentInfoBoxContent?.title"
							class="bs-product-sharing__notice-block desktop !my-24px"
							:icon="NoticeBlockIconType.Info"
						>
							<template v-slot:title>
								{{ currentInfoBoxContent.title }}
							</template>
							<template v-slot:description>
								<div v-html="currentInfoBoxContent.description"></div>
							</template>
						</BsNoticeBlock>

						<div class="my-24px h-[2px] w-full bg-n-100"></div>

						<ReuseAboutShoppingContent
							v-if="shoppingItems.length && postService !== 'bns'"
							:shoppingItems="shoppingItems"
						/>

						<BaseAttentionBox
							v-if="postService === 'bns'"
							size="medium"
							:buttonText="
								text?.newUI.purchaseUnavailableAttentionBox.buttonText
							"
							@onClickButton="handlePurchaseUnavailableAttentionBoxClick"
						>
							<template #title>
								{{ text?.newUI.purchaseUnavailableAttentionBox.title }}
							</template>
							<template #description>
								{{ text?.newUI.purchaseUnavailableAttentionBox.description }}
							</template>
						</BaseAttentionBox>
					</div>
				</template>
				<template
					v-if="
						!isLoading && !isShowContentBlock && detail && !isShowDesktopLayout
					"
				>
					<div
						:class="[
							'bs-product-sharing-post-card__nav',
							{ 'bs-product-sharing-post-card__nav--between': isTypeModal },
						]"
					>
						<span v-if="isTypeModal" @click="goBack">
							<i class="bs-icon bs-icon--back--circle"></i>
						</span>
						<div class="flex items-center gap-12px">
							<div
								class="z-[1] rounded-full bg-white p-4px shadow-[0px_4px_10px_0px_rgba(0,0,0,0.10)]"
								@click="action('share')"
							>
								<div
									ref="shareCopyRef"
									:data-clipboard-text="
										getProductSharingUrl('share-post', { id: detail.id })
									"
								>
									<BaseIcon name="upload" class="h-24px w-24px fill-n-950" />
								</div>
							</div>

							<div
								class="z-[1] rounded-full bg-white p-4px shadow-[0px_4px_10px_0px_rgba(0,0,0,0.10)]"
								@click="action('bookmark')"
							>
								<BaseIcon
									:name="detail.isBookmarked ? 'save_filled' : 'save'"
									class="h-24px w-24px fill-n-950"
								/>
							</div>
						</div>
					</div>
					<BsGallerySlideshow
						:mediaList="postImageList"
						:textClose="text?.gallery.close"
						:imgAltText="imgAltText"
					/>
					<div v-if="detail" class="bs-product-sharing-post-card__main">
						<div
							v-if="isSupportExpressCheckout"
							class="mb-16px flex items-center gap-[10px]"
						>
							<div class="rounded-[4px] bg-[#FF760A] p-[3px]">
								<BaseIcon name="easybuy" class="h-[18px] w-[18px] fill-white" />
							</div>
							<span class="typography-s2-emp text-[#FF760A]">
								{{ text?.service.title.easybuy }}
							</span>
						</div>

						<div
							class="mb-24px text-[1.125rem] font-[500] leading-[1.5rem] tracking-[0.02188rem] text-[#262626]"
						>
							{{
								productInfo.find(item => item.icon === 'product')?.value || ''
							}}
						</div>

						<div
							class="mb-16px flex w-max items-center gap-8px border border-n-100 bg-white p-[10px]"
							:class="[
								likeCountDisplay !== '0' ? 'rounded-[50px]' : 'rounded-full',
							]"
							@click="() => action('like')"
						>
							<BaseIcon
								:name="detail.isLiked ? 'like_filled' : 'like_hollow'"
								class="h-16px w-16px"
								:class="[detail.isLiked ? 'fill-[#FF5C00]' : 'fill-n-950']"
							/>
							<span
								v-if="likeCountDisplay !== '0'"
								class="typography-l2-emp text-[14px] text-n-950"
							>
								{{ likeCountDisplay }}
							</span>
						</div>

						<div
							class="mb-48px rounded-[8px] border border-n-100 bg-white px-[16px] py-[20px] mdp:p-24px"
							v-if="!isLoading"
						>
							<ReusePostAuthorContent />

							<div class="my-[24px] h-[1px] w-full bg-n-100"></div>

							<div class="typography-s2-emp mb-16px text-n-950">
								{{ text?.newUI.shipmentSectionTitle }}
							</div>
							<ReusePostDetailContent />

							<div class="my-[24px] h-[1px] w-full bg-n-100"></div>

							<ReusePostBottomContent />
						</div>

						<BsNoticeBlock
							v-if="isSupportExpressCheckout && currentInfoBoxContent?.title"
							class="bs-product-sharing__notice-block mobile !mb-48px"
							:icon="NoticeBlockIconType.Info"
						>
							<template v-slot:title>
								{{ currentInfoBoxContent.title }}
							</template>
							<template v-slot:description>
								<div v-html="currentInfoBoxContent.description"></div>
							</template>
						</BsNoticeBlock>

						<ReuseAboutShoppingContent
							v-if="shoppingItems.length && postService !== 'bns'"
							:shoppingItems="shoppingItems"
						/>

						<BaseAttentionBox
							v-if="postService === 'bns'"
							size="medium"
							:buttonText="
								text?.newUI.purchaseUnavailableAttentionBox.buttonText
							"
							@onClickButton="handlePurchaseUnavailableAttentionBoxClick"
						>
							<template #title>
								{{ text?.newUI.purchaseUnavailableAttentionBox.title }}
							</template>
							<template #description>
								{{ text?.newUI.purchaseUnavailableAttentionBox.description }}
							</template>
						</BaseAttentionBox>

						<template v-if="!isLoading && isRecommendShow">
							<div class="bs-product-sharing-post-card__recommend">
								<div class="bs-product-sharing-post-card__recommend__title">
									{{ text?.recommend.title }}
								</div>
								<ProductSharingPostList
									:text="postListTextInfo"
									:isRecommend="true"
									:columnCount="2"
									:currentPostId="detail.id"
									:postPerPage="productSharingPostPerPage.recommend"
									:isTypeModal="isTypeModal"
									@setIsRecommendShow="setIsRecommendShow"
								></ProductSharingPostList>
							</div>
						</template>
					</div>
					<BsStickyBottomContainer
						class="border-t border-n-100 !p-24px !shadow-none"
					>
						<div class="flex items-center justify-between">
							<div
								v-if="postService !== 'easybuy' && detail.itemInfo.discounted"
							>
								<BaseTag
									v-if="detail.itemInfo.discounted !== 'discount_not_available'"
									variant="info"
									class="flex items-center px-0 [&_.discounted]:text-[0.79363rem] [&_.discounted]:font-[700] [&_.discounted]:leading-[1.13375rem] [&_.discounted]:text-t-600"
									v-html="displayedDiscountFromReportedPrice"
								></BaseTag>
								<BaseTag v-else variant="info" class="flex items-center px-0">
									{{ text?.newUI.notAvailableOnLocal }}
								</BaseTag>
							</div>

							<div
								v-if="
									postService !== 'easybuy' &&
									detail.itemInfo.discounted &&
									detail.itemInfo.discounted !== 'discount_not_available'
								"
								class="typography-l1-emp text-[#7F7F7F] line-through"
							>
								{{ reportedLocalPrice }}
							</div>
						</div>

						<div class="mb-8px flex items-center justify-between">
							<div
								v-if="postService !== 'easybuy'"
								class="flex items-center gap-4px"
							>
								<div class="typography-s2-reg text-n-500">
									{{ text?.newUI.estimatedPrice }}
								</div>

								<BaseIcon
									v-tooltip="{
										content: tooltipContent,
										placement: 'top',
										popperClass:
											'max-w-[300px] whitespace-pre-line bg-black rounded-lg',
										autoHide: true,
										distance: 8,
										triggers: ['click'],
										html: true,
									}"
									name="suggest-Info"
									class="h-16px w-16px fill-n-500"
								/>
							</div>

							<span class="filler-block-only"></span>
							<div class="typography-h4-str text-black">
								{{ priceLocalPost?.replace('≈ ', '') }}
							</div>
						</div>

						<!-- Be careful when change id name here, might affect AuthSuccess.vue -->
						<BaseButton
							class="mb-8px w-full"
							id="success-dialog-primary-button"
							size="large"
							@click="postServiceAction"
						>
							<template #leadingIcon v-if="postService === 'b4u'">
								<BaseIcon name="cart_v3" class="h-16px w-16px fill-white" />
							</template>

							{{ text?.service.action[postService] }}
						</BaseButton>

						<BaseButton
							v-if="postService === 'b4u' || postService === 'bns'"
							class="w-full"
							variant="secondary"
							size="large"
							@click="handleBottomSecondaryButtonClick"
						>
							{{
								postService === 'b4u'
									? text?.newUI.buttons.viewSite
									: text?.newUI.buttons.viewWarehouse
							}}
						</BaseButton>
					</BsStickyBottomContainer>
				</template>
			</div>
			<template
				v-if="
					!isLoading &&
					!isShowContentBlock &&
					isShowDesktopLayout &&
					isRecommendShow
				"
			>
				<div class="bs-product-sharing-post-card__recommend desktop">
					<div class="bs-product-sharing-post-card__recommend__title">
						{{ text?.recommend.title }}
					</div>
					<ProductSharingPostList
						:text="postListTextInfo"
						:isRecommend="true"
						:currentPostId="detail.id"
						:columnCount="5"
						:postPerPage="productSharingPostPerPage.recommend"
						:isTypeModal="isTypeModal"
						@setIsRecommendShow="setIsRecommendShow"
					></ProductSharingPostList>
				</div>
			</template>

			<DialogFull
				v-model="isShowShippingHowItWork"
				:isForWebComponentShipping="true"
				@on-hide="hideShippingHowItWork"
			>
				<template #content>
					<div
						class="mb-32px hidden pl-16px text-[24px] leading-[32px] tracking-normal text-n-950 lg:block"
					>
						{{ shippingHowItWorkTitle }}
					</div>
					<BsHowItWorksSkeleton
						v-if="isShippingWebComponentLoading"
					></BsHowItWorksSkeleton>
					<web-component-checkout-shipping-how-it-works-view
						v-show="!isShippingWebComponentLoading"
					></web-component-checkout-shipping-how-it-works-view>
				</template>
			</DialogFull>

			<Teleport to="body">
				<BsSideDrawer
					v-model="isShowDrawerRestrictions"
					:title="sideDrawerTitleRestrictions"
					:isShowTrailing="true"
				>
					<BsHowItWorksSkeleton v-if="isWebComponentLoading" />
					<div v-show="!isWebComponentLoading">
						<web-component-post-reminder-how-it-works-view v-pre />
					</div>
				</BsSideDrawer>
			</Teleport>

			<Teleport to="head" v-if="jsonLdFormat">
				<component :is="'script'" type="application/ld+json">
					{{ jsonLdFormat }}
				</component>
			</Teleport>
		</div>
	</div>
</template>
